import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { MetasColaboradorService } from '../services/metasColaborador.service';
import { COMENTARIO_MIN_POR_OMISION, ResultadoResolver } from '../types/metas.types';

interface Props {
  lunes: string;
  chain: string;
  num: number;
  /** La versión que se está viendo: si otro la movió, la API responde 409. */
  version: number;
  /** `mi-alcance.parametros.comentarioMin` (10 por omisión). */
  comentarioMin?: number;
  onReabierta: (r: ResultadoResolver) => void;
  /** El error de la API (409 si cambió, 422 si el comentario es corto, 403 sin rol). */
  onError?: (e: unknown) => void;
}

/** Cuántos caracteres lleva el comentario: el mismo recorte que hace la API. */
export const largoComentario = (c: string): number => c.trim().length;

/**
 * «Reabrir» una tienda AUTORIZADA (vuelve a borrador). Sólo administradores:
 * la API lo exige por ROL y la pantalla sólo lo ofrece con
 * `permisos.reabrir`. Pide el comentario —queda en la bitácora con quién la
 * había autorizado— y se confirma con un segundo botón: una autorización no
 * se deshace de un clic.
 */
export const ReabrirControl: React.FC<Props> = ({
  lunes,
  chain,
  num,
  version,
  comentarioMin = COMENTARIO_MIN_POR_OMISION,
  onReabierta,
  onError,
}) => {
  const [abierto, setAbierto] = useState(false);
  const [comentario, setComentario] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const minimo = Math.max(1, comentarioMin);
  const largo = largoComentario(comentario);
  const corto = largo < minimo;

  const reabrir = async () => {
    if (corto) return;
    setTrabajando(true);
    try {
      const r = await MetasColaboradorService.reabrir(lunes, chain, num, { version, comentario });
      setAbierto(false);
      setComentario('');
      onReabierta(r);
    } catch (e) {
      onError?.(e);
    } finally {
      setTrabajando(false);
    }
  };

  if (!abierto)
    return (
      <Button
        label="Reabrir"
        icon="pi pi-lock-open"
        severity="warning"
        outlined
        size="small"
        onClick={() => setAbierto(true)}
        title="Regresa la tienda a borrador para corregirla (sólo administradores)"
      />
    );

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-left" data-testid="reabrir-control">
      <Message
        severity="warn"
        text="La tienda vuelve a borrador: el supervisor la corrige y la reenvía, y el regional la autoriza otra vez."
      />
      <label htmlFor={`comentario-reabrir-${chain}-${num}`} className="font-semibold">
        Por qué se reabre
      </label>
      <InputTextarea
        id={`comentario-reabrir-${chain}-${num}`}
        value={comentario}
        onChange={(e) => setComentario(e.target.value)}
        rows={3}
        maxLength={1000}
        autoResize
        className={`w-full ${comentario && corto ? 'p-invalid' : ''}`}
      />
      <small className={corto ? 'text-amber-800' : 'text-gray-600'} data-testid="contador-reabrir">
        {largo} / {minimo} caracteres como mínimo
      </small>
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          label="Cancelar"
          text
          size="small"
          onClick={() => {
            setAbierto(false);
            setComentario('');
          }}
          disabled={trabajando}
        />
        <Button
          label="Confirmar reabrir"
          icon="pi pi-lock-open"
          severity="warning"
          size="small"
          disabled={corto || trabajando}
          loading={trabajando}
          onClick={() => void reabrir()}
        />
      </div>
    </div>
  );
};

export default ReabrirControl;
